
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";

interface ImageUploaderProps {
  routineId: string;
  refetchRoutines: () => void;
}

export const ImageUploader = ({ routineId, refetchRoutines }: ImageUploaderProps) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Por favor, sube solo archivos de imagen.",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "La imagen es demasiado grande. El tamaño máximo es 5MB.",
      });
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('routineId', routineId);

      const { data, error } = await supabase.functions.invoke('upload-routine-image', {
        body: formData,
      });

      if (error) throw error;

      toast({
        title: "Imagen subida",
        description: "La imagen se ha subido correctamente.",
      });
      refetchRoutines();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo subir la imagen. Por favor, intenta de nuevo.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadClick = () => {
    const fileInput = document.getElementById(`image-upload-${routineId}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  return (
    <div className="relative">
      <input
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
        id={`image-upload-${routineId}`}
        disabled={isUploading}
      />
      <Button
        variant="outline"
        size="icon"
        className="border-white/20 hover:bg-clubbers-red/10 hover:border-clubbers-red text-white"
        disabled={isUploading}
        onClick={handleUploadClick}
      >
        <Upload className="h-4 w-4" />
      </Button>
    </div>
  );
};
